/**
 * HearingOutcomeCard.jsx — the Permitting Coordinator's hearing cards in the daily triage
 * (TriageGate.jsx; api/_hearings.js; docs/HEARING_OUTCOMES.md; Matt, 2026-10-23).
 *
 * hearingOptions(card): the morning after a hearing — Approved / Approved with conditions /
 * Continued / Denied. Each opens one form: the hearing date, the board (size, appeal days, story
 * poles, members), the vote, the conditions and the milestone they ride, the memo that goes on the
 * permit log, and the two emails (to the client on an approval, and the thank-you to the board on any
 * outcome), each shown in full and editable, with who it goes to and that it goes from the
 * coordinator. Nothing is sent that is not on screen.
 *
 * appealOptions(card): the appeal period ended — No appeal filed / An appeal was filed.
 *
 * Words come from api/_hearingWords.js, the same file the server and the Client Hub card read.
 */
import { recordHearingOutcome, recordAppealOutcome } from '../services/passengers.js'
import { OUTCOMES, isApproval, appealEndsOn, fmtDate, fmtDayDate, cityOf, memoDraft, clientEmailDraft, boardEmailDraft } from '../../../api/_hearingWords.js'

const C = {
  label: { fontFamily: '"DM Sans",sans-serif', fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#5b8494', marginBottom: 6 },
  section: { borderTop: '0.5px solid rgba(7,39,45,0.14)', paddingTop: 12, marginTop: 12 },
  row: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  small: { fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: '#3a5260', lineHeight: 1.45 },
  num: { font: 'inherit', fontFamily: '"DM Mono",monospace', fontSize: 13, width: 54, padding: '7px 8px', borderRadius: 6, border: '0.5px solid rgba(7,39,45,0.3)', color: '#07272D' },
  input: { font: 'inherit', fontFamily: '"DM Sans",sans-serif', fontSize: 13, padding: '7px 9px', borderRadius: 6, border: '0.5px solid rgba(7,39,45,0.3)', color: '#07272D', boxSizing: 'border-box' },
  link: { font: 'inherit', fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, color: '#07272D', background: 'none', border: 'none', padding: '4px 0', cursor: 'pointer', textDecoration: 'underline' },
  check: { display: 'inline-flex', alignItems: 'center', gap: 7, fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: '#07272D', cursor: 'pointer' },
  sendNote: { fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, color: '#07272D', background: 'rgba(240,178,122,0.16)', borderRadius: 6, padding: '8px 10px', marginBottom: 8, lineHeight: 1.45 },
}

const firstName = (n) => String(n || '').trim().split(/\s+/)[0] || ''

function defaultsFor(card, outcome, me) {
  const body = card.bodies && card.bodies[0]
  return {
    hearingDate: card.hearingDate || '',
    vote: { for: '', against: '', absent: '' },
    body: body ? { ...body, members: body.members.map(m => ({ ...m })) } : { id: null, name: '', boardSize: '', appealDays: '', storyPoles: false, members: [] },
    conditions: outcome === 'approved_conditions' ? [''] : [],
    conditionsMilestoneId: card.defaultConditionsMilestoneId || '',
    continuedTo: '',
    memo: null,                                 // null = follow the draft
    client: { send: isApproval(outcome) && card.recipients.length > 0, to: card.recipients.map(r => r.email), subject: null, body: null },
    board: { send: true, subject: null, body: null },
    me,
  }
}

/** The final words, from what's on screen: the draft unless the coordinator edited it. */
function finalize(card, outcome, g) {
  const appealEnds = isApproval(outcome) ? appealEndsOn(g.hearingDate, g.body.appealDays) : null
  const conditions = (g.conditions || []).map(s => s.trim()).filter(Boolean)
  const greetNames = card.recipients.filter(r => g.client.to.includes(r.email)).map(r => firstName(r.name)).filter(Boolean)
  const greeting = card.clientGreeting || (greetNames.length ? greetNames.slice(0, -1).join(', ') + (greetNames.length > 1 ? ' and ' : '') + greetNames[greetNames.length - 1] : '')
  const facts = { outcome, projectName: card.projectName, projectAddress: card.projectAddress, bodyName: g.body.name, agencyCity: cityOf(card.projectAddress),
    hearingDate: g.hearingDate, vote: g.vote, conditions, appealEnds, storyPoles: g.body.storyPoles, continuedTo: g.continuedTo, coordinatorName: g.me.name, greeting }
  const cd = clientEmailDraft(facts) || { subject: '', body: '' }
  const bd = boardEmailDraft(facts)
  return {
    appealEnds, conditions,
    memo: g.memo != null ? g.memo : memoDraft(facts),
    clientSubject: g.client.subject != null ? g.client.subject : cd.subject,
    clientBody: g.client.body != null ? g.client.body : cd.body,
    boardSubject: g.board.subject != null ? g.board.subject : bd.subject,
    boardBody: g.board.body != null ? g.board.body : bd.body,
  }
}

function boardEmails(g) { return (g.body.members || []).map(m => String(m.email || '').trim()).filter(e => /@/.test(e)) }

function HearingForm({ card, outcome, g, setG }) {
  const f = finalize(card, outcome, g)
  const up = (patch) => setG({ ...g, ...patch })
  const upBody = (patch) => setG({ ...g, body: { ...g.body, ...patch } })
  const approval = isApproval(outcome)
  const boardTo = boardEmails(g)
  const clientTo = card.recipients.filter(r => g.client.to.includes(r.email))
  const me = g.me.email ? `${g.me.name} (${g.me.email})` : g.me.name || 'you'
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={C.row}>
        <div>
          <div style={C.label}>Hearing date</div>
          <input type="date" className="aj-tt-date" style={{ marginBottom: 0 }} value={g.hearingDate} onChange={e => up({ hearingDate: e.target.value })} />
        </div>
        {outcome === 'continued' && (
          <div>
            <div style={C.label}>Continued to</div>
            <input type="date" className="aj-tt-date" style={{ marginBottom: 0 }} value={g.continuedTo} min={g.hearingDate} onChange={e => up({ continuedTo: e.target.value })} />
          </div>
        )}
      </div>

      <div style={C.section}>
        <div style={C.label}>The board</div>
        <div style={C.row}>
          <input style={{ ...C.input, flex: '1 1 200px' }} placeholder="e.g. Design Review Board" value={g.body.name} onChange={e => upBody({ name: e.target.value })} />
          <span style={C.small}>Members</span>
          <input style={C.num} inputMode="numeric" value={g.body.boardSize ?? ''} onChange={e => upBody({ boardSize: e.target.value.replace(/\D/g, '') })} />
          <span style={C.small}>Appeal period, days</span>
          <input style={C.num} inputMode="numeric" value={g.body.appealDays ?? ''} onChange={e => upBody({ appealDays: e.target.value.replace(/\D/g, '') })} />
        </div>
        <label style={{ ...C.check, marginTop: 8 }}>
          <input type="checkbox" checked={!!g.body.storyPoles} onChange={e => upBody({ storyPoles: e.target.checked })} />
          Story poles stay up until the appeal period ends
        </label>
        <div style={{ ...C.small, marginTop: 6 }}>Saved for {card.agencyName || 'this agency'}{'’'}s next hearing too.</div>
      </div>

      {outcome !== 'continued' && (
        <div style={C.section}>
          <div style={C.label}>The vote</div>
          <div style={C.row}>
            <span style={C.small}>For</span>
            <input style={C.num} inputMode="numeric" autoFocus value={g.vote.for} onChange={e => up({ vote: { ...g.vote, for: e.target.value.replace(/\D/g, '') } })} />
            <span style={C.small}>Against</span>
            <input style={C.num} inputMode="numeric" value={g.vote.against} onChange={e => up({ vote: { ...g.vote, against: e.target.value.replace(/\D/g, '') } })} />
            <span style={C.small}>Absent</span>
            <input style={C.num} inputMode="numeric" value={g.vote.absent} onChange={e => up({ vote: { ...g.vote, absent: e.target.value.replace(/\D/g, '') } })} />
          </div>
          {approval && f.appealEnds && (
            <div style={{ ...C.small, marginTop: 8 }}>
              Appeal period ends <b>{fmtDayDate(f.appealEnds)}</b>. A card comes back to you the day after to say whether anyone appealed.
            </div>
          )}
        </div>
      )}

      {outcome === 'approved_conditions' && (
        <div style={C.section}>
          <div style={C.label}>Conditions of approval</div>
          {g.conditions.map((c, i) => (
            <div key={i} style={{ ...C.row, marginBottom: 6 }}>
              <input style={{ ...C.input, flex: 1 }} placeholder="One condition, as the board stated it" value={c}
                onChange={e => { const n = [...g.conditions]; n[i] = e.target.value; up({ conditions: n }) }} />
              {g.conditions.length > 1 && <button type="button" style={C.link} onClick={() => up({ conditions: g.conditions.filter((_, j) => j !== i) })}>Remove</button>}
            </div>
          ))}
          <button type="button" style={C.link} onClick={() => up({ conditions: [...g.conditions, ''] })}>+ Another condition</button>
          <div style={{ ...C.row, marginTop: 8 }}>
            <span style={C.small}>Each becomes a thread riding</span>
            <select style={{ ...C.input, maxWidth: 340 }} value={g.conditionsMilestoneId} onChange={e => up({ conditionsMilestoneId: e.target.value })}>
              <option value="">(no milestone)</option>
              {card.milestones.map(m => <option key={m.id} value={m.id}>{m.name}{m.phase ? ' · ' + m.phase.replace(/ Phase$/, '') : ''}</option>)}
            </select>
          </div>
          <div style={{ ...C.small, marginTop: 6 }}>The building permit submission{'’'}s kit will also ask for the conditions of approval to be attached to the plan set.</div>
        </div>
      )}

      <div style={C.section}>
        <div style={C.label}>On the permit log{approval || outcome === 'continued' ? ' (the client sees this on their Permitting card)' : ''}</div>
        {card.logged && ((card.logged === 'approved' && approval) || (card.logged === 'continued' && outcome === 'continued'))
          ? <div style={C.small}>Already logged on the permit: “{String(card.remarks || card.activityType).trim()}” It stays as it is.</div>
          : <textarea className="aj-tt-text" style={{ marginBottom: 0, minHeight: 52 }} value={f.memo} onChange={e => up({ memo: e.target.value })} />}
      </div>

      {approval && (
        <div style={C.section}>
          <div style={C.label}>Email to the client</div>
          {!card.hasHub ? <div style={C.small}>This project has no Client Hub, so there is no card or email. Call or write to the client yourself.</div>
            : !card.recipients.length ? <div style={C.small}>Nobody is on this project{'’'}s Client Hub yet, so no email goes out. The celebration card still posts.</div>
            : card.hubHeld ? <div style={C.small}>A game-changing comment is holding this project{'’'}s client updates, so the card is saved but held and no email goes out.</div>
            : <>
              <label style={{ ...C.check, marginBottom: 8, display: 'flex' }}>
                <input type="checkbox" checked={g.client.send} onChange={e => up({ client: { ...g.client, send: e.target.checked } })} />
                Send it
              </label>
              {g.client.send && <>
                <div style={C.sendNote}>
                  This goes out <b>from you</b>, {me}, when you press the button below, to{' '}
                  {clientTo.length ? clientTo.map(r => r.name ? `${r.name} (${r.email})` : r.email).join(', ') : 'nobody yet'}. You are copied, and replies come to you.
                </div>
                <div style={{ ...C.row, marginBottom: 8 }}>
                  {card.recipients.map(r => (
                    <label key={r.email} style={C.check}>
                      <input type="checkbox" checked={g.client.to.includes(r.email)}
                        onChange={e => up({ client: { ...g.client, to: e.target.checked ? [...g.client.to, r.email] : g.client.to.filter(x => x !== r.email) } })} />
                      {r.name || r.email}
                    </label>
                  ))}
                </div>
                <input style={{ ...C.input, width: '100%', marginBottom: 6 }} value={f.clientSubject} onChange={e => up({ client: { ...g.client, subject: e.target.value } })} />
                <textarea className="aj-tt-text" style={{ minHeight: 220, marginBottom: 0 }} value={f.clientBody} onChange={e => up({ client: { ...g.client, body: e.target.value } })} />
                <div style={{ ...C.small, marginTop: 4 }}>A button to their Client Hub is added below your note.</div>
              </>}
            </>}
        </div>
      )}

      <div style={C.section}>
        <div style={C.label}>Thank-you to the board</div>
        <label style={{ ...C.check, marginBottom: 8, display: 'flex' }}>
          <input type="checkbox" checked={g.board.send} onChange={e => up({ board: { ...g.board, send: e.target.checked } })} />
          Send it
        </label>
        {g.board.send && <>
          {(g.body.members || []).map((m, i) => (
            <div key={i} style={{ ...C.row, marginBottom: 6 }}>
              <input style={{ ...C.input, flex: '1 1 140px' }} placeholder="Name" value={m.name}
                onChange={e => { const n = g.body.members.map((x, j) => j === i ? { ...x, name: e.target.value } : x); upBody({ members: n }) }} />
              <input style={{ ...C.input, flex: '2 1 200px' }} placeholder="Email" value={m.email}
                onChange={e => { const n = g.body.members.map((x, j) => j === i ? { ...x, email: e.target.value } : x); upBody({ members: n }) }} />
              <button type="button" style={C.link} onClick={() => upBody({ members: g.body.members.filter((_, j) => j !== i) })}>Remove</button>
            </div>
          ))}
          <div><button type="button" style={C.link} onClick={() => upBody({ members: [...(g.body.members || []), { name: '', email: '' }] })}>+ Add a board member</button></div>
          <div style={{ ...C.small, margin: '2px 0 8px' }}>The members are saved with the board for next time.</div>
          {!boardTo.length && <div style={{ ...C.small, marginBottom: 8 }}>Add the members{'’'} email addresses to send this.</div>}
          {boardTo.length > 0 && <div style={C.sendNote}>This goes out <b>from you</b>, {me}, to the {boardTo.length} board member{boardTo.length === 1 ? '' : 's'} above, with you copied.</div>}
          <input style={{ ...C.input, width: '100%', marginBottom: 6 }} value={f.boardSubject} onChange={e => up({ board: { ...g.board, subject: e.target.value } })} />
          <textarea className="aj-tt-text" style={{ minHeight: 150, marginBottom: 0 }} value={f.boardBody} onChange={e => up({ board: { ...g.board, body: e.target.value } })} />
        </>}
      </div>
    </div>
  )
}

function ready(outcome, g) {
  if (!g || !/^\d{4}-\d{2}-\d{2}$/.test(g.hearingDate) || !String(g.body.name || '').trim()) return false
  if (isApproval(outcome) && (g.vote.for === '' || g.body.appealDays === '' || g.body.appealDays == null)) return false
  if (outcome === 'approved_conditions' && !g.conditions.some(c => c.trim())) return false
  if (outcome === 'continued' && !/^\d{4}-\d{2}-\d{2}$/.test(g.continuedTo)) return false
  return true
}

function confirmLabel(card, outcome, g) {
  if (!g) return 'Record it'
  const sends = []
  if (isApproval(outcome) && card.hasHub && !card.hubHeld && g.client.send && g.client.to.length) sends.push('email the client')
  if (g.board.send && boardEmails(g).length) sends.push('thank the board')
  if (isApproval(outcome)) return 'Record and celebrate' + (sends.length ? ', ' + sends.join(' and ') : '')
  return 'Record it' + (sends.length ? ' and ' + sends.join(' and ') : '')
}

const BODY = {
  approved: (c) => `Logged on the permit, the hearing milestones post, the appeal clock starts, a celebration card goes up on ${c.projectName}’s Client Hub and the project channel hears it.`,
  approved_conditions: (c) => `Everything an approval does for ${c.projectName}, and each condition becomes a thread riding the milestone you pick.`,
  continued: () => 'Logged on the permit with the new hearing date. This card comes back the day after it.',
  denied: () => 'Logged on the permit (not on the client’s card) and the team hears it in Slack. Call the client yourself; nothing goes to them from here.',
}
const ICON = { approved: 'check', approved_conditions: 'did', continued: 'clock', denied: 'dayoff' }
const SUB = {
  approved: 'The board approved it',
  approved_conditions: 'Approved, with conditions to carry',
  continued: 'Comes back on a later date',
  denied: 'Not approved',
}

export function hearingOptions(card, me) {
  return ['approved', 'approved_conditions', 'continued', 'denied'].map(outcome => ({
    key: outcome, icon: ICON[outcome], label: OUTCOMES[outcome], sub: SUB[outcome],
    tone: outcome === 'approved' ? 'primary' : outcome === 'denied' ? 'caution' : undefined,
    step: {
      kind: 'custom', defaultValue: defaultsFor(card, outcome, me), title: `${OUTCOMES[outcome]}: ${card.projectName}`, body: BODY[outcome](card),
      render: (g, setG) => <HearingForm card={card} outcome={outcome} g={g} setG={setG} />,
      ready: (g) => ready(outcome, g), confirm: (g) => confirmLabel(card, outcome, g), confirmIcon: ICON[outcome],
    },
    run: (g) => {
      const f = finalize(card, outcome, g)
      return recordHearingOutcome(me.id, me.name, {
        activityId: card.activityId, reviewId: card.reviewId, projectId: card.projectId, projectName: card.projectName,
        agencyId: card.agencyId, agencyName: card.agencyName, logged: card.logged, noticedOn: card.noticedOn,
        hearingDate: g.hearingDate, outcome, vote: g.vote, body: g.body, conditions: f.conditions,
        conditionsMilestoneId: g.conditionsMilestoneId || null, continuedTo: g.continuedTo || null, memo: f.memo,
        clientEmail: { send: isApproval(outcome) && g.client.send, to: g.client.to, subject: f.clientSubject, body: f.clientBody },
        boardEmail: { send: g.board.send, subject: f.boardSubject, body: f.boardBody },
      })
    },
    done: (r) => (r && r.report && r.report.length ? r.report.join(' · ') : 'Recorded.'),
  }))
}

export function appealOptions(card, me) {
  return [
    { key: 'none', icon: 'check', label: 'No appeal filed', sub: 'The approval is final', tone: 'primary',
      step: { kind: 'confirm', title: 'No appeal was filed', body: `Logged on the permit, which updates ${card.projectName}’s Permitting card for the client. The Client Hub celebration card says the approval is final${card.storyPoles ? ' and the story poles can come down' : ''}, and the team hears it in Slack.`, confirm: () => 'It’s final', confirmIcon: 'check' },
      run: () => recordAppealOutcome(me.id, me.name, { outcomeId: card.outcomeId, decision: 'none' }),
      done: () => 'Recorded. The approval is final.' },
    { key: 'filed', icon: 'escalate', label: 'An appeal was filed', sub: 'Tell the team; call the client', tone: 'caution',
      step: { kind: 'custom', defaultValue: { note: '', date: '' }, title: 'An appeal was filed', body: 'Logged on the permit and posted to the team in Slack. Nothing goes to the client from here; call them yourself.',
        render: (g, setG) => (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 12 }}>
            <textarea className="aj-tt-text" autoFocus style={{ marginBottom: 0, minHeight: 60 }} value={g.note} placeholder="Who appealed, and on what grounds, as far as you know" onChange={e => setG({ ...g, note: e.target.value })} />
            <div style={C.row}>
              <span style={C.small}>Appeal hearing date (if set)</span>
              <input type="date" className="aj-tt-date" style={{ marginBottom: 0 }} value={g.date} onChange={e => setG({ ...g, date: e.target.value })} />
            </div>
          </div>
        ),
        ready: (g) => !!(g && g.note.trim()), confirm: () => 'Record the appeal', confirmIcon: 'escalate' },
      run: (g) => recordAppealOutcome(me.id, me.name, { outcomeId: card.outcomeId, decision: 'filed', note: g.note, appealHearingDate: g.date || null }),
      done: () => 'Recorded. The team has been told.' },
  ]
}
